/**
 * detection-expander.js
 */

import React from 'react';
import {
  alpha,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Icon from 'components/icon';
import Button from 'components/button';
import { DetectionOdorsContext } from '../context/detection-odors-context';
import { sectionTypes } from './detection-common';


const collapsedHeight = 48;

const useStyles = makeStyles(theme => ({
  expander: {
    flex: 1,
    border: `2px solid ${theme.palette.grey.colors[7]}`,
    borderRadius: '25px 0 0 25px !important',
    backgroundColor: theme.palette.sys.surfaceContainerHigh,
    boxShadow: 'none',
    transition: `100ms ease-in-out`,
    zIndex: 2,
    overflow: 'hidden',
    '&:before': {
      display: 'none'
    },
    '&:hover': {
      backgroundColor: theme.palette.sys.surfaceContainerHighest
    },
    '&.Mui-expanded': {
      margin: 0,
      backgroundColor: theme.palette.sys.surfaceContainerHigh
    },
    '&.error': {
      backgroundColor: alpha(theme.palette.error.light, 0.3),
      '&:not(.Mui-expanded):hover': {
        backgroundColor: alpha(theme.palette.error.light, 0.4)
      }
    },
    '&.odor-env': {
      marginLeft: theme.spacing(4)
    },
    '&.odor': {
      marginLeft: theme.spacing(8)
    },
  },
  expanderSummary: {
    '&.Mui-expanded': {
      minHeight: collapsedHeight,
      height: collapsedHeight,
    },
  },
  expanderSummaryContent: {
    minHeight: collapsedHeight,
    height: collapsedHeight,
    width: '100%',
    margin: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
    '& .summary-left': {
      overflow: 'hidden',
      marginRight: theme.spacing(2),
      '& .label': {
        marginLeft: theme.spacing(1.5),
        opacity: 0,
        transition: '275ms ease-in-out',
        overflow: 'hidden',
        '& > *': {
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .type': {
          fontWeight: 400,
          fontSize: theme.typography.sizes[1],
          textTransform: 'uppercase',
        }
      }
    },
    '& .summary-right': {
      maxHeight: '100%',
      position: 'relative',
      '& > .anim': {
        position: 'relative',
        height: collapsedHeight,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        transform: 'translateY(-100%)',
        transition: '275ms ease-in-out',
      },
      '& .collapsed': {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '& .expanded': {
        opacity: 0,
        pointerEvents: 'none',
      },
      '& .add-label': {
        position: 'absolute',
        right: 0,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      },
    },
    '&.Mui-expanded, &:not(.is-add)': {
      '& .summary-left .label': {
        opacity: 1
      }
    },
    '&.Mui-expanded': {
      margin: 'inherit',
      '& .summary-right': {
        '& .anim': {
          transform: 'translateY(0%)',
        },
        '& .collapsed': {
          opacity: 0,
          pointerEvents: 'none',
        },
        '& .expanded': {
          opacity: 1,
          pointerEvents: 'auto',
        }
      }
    },
    '&.is-add .summary-left .pt-icon': {
      color: theme.palette.sys.onSurfaceVariant
    }
  },
  expanderAdd: {
    border: `2px dashed ${theme.palette.sys.outlineVariant}`,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    color: props => props.disabled ? theme.palette.grey.colors[7] : 'inherit',
    '&:not(.Mui-expanded) .MuiTypography-root': {
      transition: `100ms ease-in-out`,
    },
    '&:not(.Mui-expanded):hover': {
      backgroundColor: theme.palette.sys.surfaceContainerHighest
    },
    '&:not(.Mui-expanded):hover .MuiTypography-root': {
    },
    '&:not(.Mui-expanded):hover .MuiSvgIcon-root': {
    },
    '& .MuiButtonBase-root': {
      cursor: props => props.disabled ? 'auto !important' : 'pointer'
    }
  },
  detail: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 500,
    padding: `${theme.spacing(1)} 0 ${theme.spacing(1)} ${theme.spacing(4)}`,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      padding: 0
    }
  }
}));


export default function DetectionExpander(props) {
  const {
    id,
    type,
    typeTitle,
    title,
    iconName,
    isNew,
    onDone,
    onCancel,
    onDelete,
    error,
    disabled,
    'data-testtype': dataTestType,
    detailClassName,
    children
  } = props;
  const classes = useStyles({disabled});
  const { expandedId, setExpandedId, currentDoneHandler, setCurrentDoneHandler } = React.useContext(DetectionOdorsContext);
  const isExpanded = id === expandedId;


  return (
    <Accordion
      className={clsx({
        [classes.expander]: true,
        [classes.expanderAdd]: isNew,
        'error': error,
        'environment': type === sectionTypes.ENVIRONMENT,
        'odor-env': type === sectionTypes.ODOR_ENVIRONMENT,
        'odor': type === sectionTypes.ODOR
      })}
      expanded={isNew && disabled ? false : isExpanded}
      onChange={async (e) => {
        if (isNew && disabled) {
          return;
        }

        // if not new then closing implies done
        if (isExpanded && !(await onDone())) {
          return;
        }

        // if other expander is open, try to save first
        if (!isExpanded && expandedId && currentDoneHandler && !(await currentDoneHandler())) {
          return;
        }

        setExpandedId(isExpanded ? null : id);
        setCurrentDoneHandler(isExpanded ? null : onDone);
      }}
      data-testtype={dataTestType}
    >
      <AccordionSummary classes={{
        root: classes.expanderSummary,
        content: `${classes.expanderSummaryContent} ${isNew ? 'is-add' : ''}`
      }}>
        <div className="flex-hc summary-left">
          <Icon type={error ? 'exclamation' : iconName} />
          <div className='label'>
            {!isNew && <Typography variant='subtitle2'>{title}</Typography>}
            <Typography variant='subtitle2' className='type'>{typeTitle}</Typography>
          </div>
        </div>
        <div className="summary-right" style={{ flex: 1 }}>

          {/* ADD DETECTION ITEM */}
          {isNew &&
          <>
            <div className="anim expanded" />
            {/* add item label */}
            <div className="anim collapsed">
              <Typography
                variant='subtitle2'
                className='add-label'
              >
                + Add {typeTitle}
              </Typography>
            </div>
          </>}

          {/* EXISTING DETECTION ITEM */}
          {!isNew &&
          <>
            <div className="anim expanded" />
            {/* delete item button */}
            <div className="anim collapsed">
              <IconButton
                style={{ marginRight: -16 }}
                disabled={disabled}
                onClick={e => {
                  e.stopPropagation();
                  onDelete();
                }}
                size="large">
                <Icon type='delete' />
              </IconButton>
            </div>
          </>}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className={`${classes.detail}${detailClassName ? ` ${detailClassName}` : ''}`}>
          {children}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between'}}>
            <Button
              variant="contained"
              color="primary"
              onClick={async (e) => {
                if (await onDone()) {
                  setExpandedId();
                  setCurrentDoneHandler();
                }
              }}
              data-testid={`detection-expander-done-button-${type}`}
              children='Done'
            />
            {onCancel &&
              <Button
                variant="outlined"
                onClick={() => {
                  onCancel();
                  setExpandedId();
                  setCurrentDoneHandler();
                }}
                children='Cancel'
              />
            }
          </Box>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}